import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Button, Drawer, Flex, rem, TextInput, Title } from '@mantine/core';
import { useEffect, useState } from 'react';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { stripTranslationMarkers } from '@dealstack/frontend/shared/mechanism/i18n/translationMarkers';
import { ConfirmationBox } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingEventDrawer/styled';
import { deleteHoldingsEvents } from '@dealstack/frontend/app/services/holdingsEvent.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';

interface Props {
	className?: string;
	opened: boolean;
	onClose: () => void;
	onSuccess?: () => void;
	refreshSecuritiesList: () => void;
	eventIds: string[];
}

export const BulkDeleteHoldingEventDrawer = ({
	opened,
	onClose,
	onSuccess,
	className,
	refreshSecuritiesList,
	eventIds = [],
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const [confirmTextValue, setConfirmTextValue] = useState('');
	const [wordInputError, setWordInputError] = useState<string>();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const confirmDeleteText = tCapManage('holdings.event.confirm_bulk');
	const expectedConfirmText = stripTranslationMarkers(confirmDeleteText);

	useEffect(() => {
		if (!opened) {
			setConfirmTextValue('');
			setWordInputError(undefined);
			setIsSubmitting(false);
		}
	}, [opened]);

	const handleConfirmDelete = async () => {
		if (confirmTextValue !== expectedConfirmText) {
			setWordInputError(tCapManage('holdings.event.match'));
			return;
		}

		if (eventIds.length > 0) {
			setIsSubmitting(true);

			try {
				await deleteHoldingsEvents(eventIds);

				showToastV2({
					message: tCapManage('holdings.event.bulk_remove'),
				});

				refreshSecuritiesList();
				onSuccess?.();
				handleClose();
			} catch {
				showToastV2({
					title: tCapManage('holdings.event.bulk_remove_fail'),
					state: 'error',
				});
			} finally {
				setIsSubmitting(false);
			}
		}
	};

	const handleInputChange = (value: string) => {
		setConfirmTextValue(value);
		setWordInputError(undefined);
	};

	const handleClose = () => {
		setConfirmTextValue('');
		setWordInputError(undefined);
		setIsSubmitting(false);
		onClose();
	};

	return (
		<Drawer
			opened={opened}
			onClose={handleClose}
			position="right"
			size="md"
			className={className}
			transitionProps={drawerTransitionProps}
			trapFocus={false}
			title={
				<Title order={4} fw={600}>
					{tCapManage('holdings.event.delete_bulk')}
				</Title>
			}
		>
			<Flex direction="column" justify="space-between" h="100%">
				<Flex direction="column" gap={rem('16px')}>
					<ConfirmationBox>
						<Text variant="sans/36/200" disableVariantMapping>
							{confirmDeleteText}
						</Text>
					</ConfirmationBox>

					<TextInput
						label={tCapManage('holdings.event.delete_enter')}
						placeholder={tCapManage('holdings.event.delete_enter_placeholder')}
						size="md"
						value={confirmTextValue}
						onChange={(event) => handleInputChange(event.currentTarget.value)}
						error={wordInputError}
					/>
				</Flex>

				<Flex direction="column" gap={rem('8px')} mt="auto">
					<Button
						variant="filled"
						color="red"
						size="md"
						onClick={handleConfirmDelete}
						fullWidth
						disabled={confirmTextValue !== expectedConfirmText || isSubmitting}
						loading={isSubmitting}
					>
						{`${tCapManage('holdings.event.delete_confirm')} (${eventIds.length})`}
					</Button>
					<Button
						onClick={handleClose}
						fullWidth
						variant="default"
						disabled={isSubmitting}
					>
						{tCapManage('holdings.event.cancel')}
					</Button>
				</Flex>
			</Flex>
		</Drawer>
	);
};
